import React, { FC, memo, useEffect, useState } from 'react'

import type { Swiper as SwiperType } from 'swiper'

import { useLinePointerNavigation } from '@/components/Lines/hooks'
import { Slider } from '@/components/Slider/Slider'
import { ChannelLoadingItem } from '@/components/Tiles/LoadingTile'
import {
  eventObject,
  eventAction,
  videoPlayerNames,
  analyticsVideoTypes,
} from '@/constants/analytics'
import { sliderMovementType } from '@/constants/common'
import { focusIds } from '@/constants/focus'
import { channelSlidersCountInPortView } from '@/constants/tiles'
import { useActiveBroadcast } from '@/hooks/useActiveBroadcast'
import { OpenTileProps } from '@/hooks/useOpenTile'
import useStore from '@/store/useStore'
import { ChannelLineProps } from '@/types/componentsPropsTypes'
import { ChannelType } from '@/types/servicesTypes/configTypes'
import { sendAnalyticsEvent } from '@/utils/analyticsUtils'

import { ChannelLineElem, LineTitle, SliderWrapper } from './styles'

import '../Slider/styles.scss'

const ChannelLineRaw: FC<ChannelLineProps> = ({
  nodeId,
  line,
  broadcastId,
  sliderData,
}) => {
  const setActiveBroadcastChannel = useStore(
    (state) => state.common.setActiveBroadcastChannel,
  )
  const { activeChannel, channels = [] } = useActiveBroadcast(broadcastId)
  const isLoaded = channels.length > 0

  const [swiper, setSwiper] = useState<Nullable<SwiperType>>(null)
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)

  const focusId = `${focusIds.CHANNEL_SLIDER}${nodeId}`

  const activeSlideId =
    activeSlideIndex !== undefined ? sliderData[activeSlideIndex]?.id : ''

  const { name } = line
  const prevChannelIndex = sliderData.findIndex(
    (el) => el.id === activeChannel?.id,
  )

  useEffect(() => {
    return () => {
      swiper?.destroy(true, true)
    }
  }, [])

  const onSwiper = (swiper: SwiperType) => {
    /**
     * Important. Place init events later in queue
     */
    setTimeout(() => {
      setSwiper(swiper)
      swiper.slideTo(activeSlideIndex, 0)
    }, 0)

    if (prevChannelIndex >= 0) {
      setActiveSlideIndex(prevChannelIndex)
      setTimeout(() => {
        swiper?.slideTo(prevChannelIndex)
      }, 0)
    }
  }

  const slideNext = (e?: LRUDEvent) => {
    e?.preventDefault()
    swiper && setActiveSlideIndex(swiper?.activeIndex)
  }

  const slidePrev = (e?: LRUDEvent) => {
    if (swiper && swiper.activeIndex >= 0) {
      e?.preventDefault()
      swiper && setActiveSlideIndex(swiper?.activeIndex)
    }
  }

  const onHandleToggle = ({ tileData }: OpenTileProps) => {
    const activeChannel = channels?.find(
      (c: ChannelType) => c.id === tileData.id,
    )
    activeChannel && setActiveBroadcastChannel(broadcastId, activeChannel)
    activeChannel &&
      sendAnalyticsEvent({
        object: eventObject.VIDEO,
        action: eventAction.START_CLICK,
        video: {
          id: activeChannel.id,
          name: activeChannel.name || activeChannel.id,
          playerName: videoPlayerNames.HLS,
          type: analyticsVideoTypes.STREAM,
          link: activeChannel.video?.sources?.[0]?.url,
          partnerIds: activeChannel.partnerIds,
        },
      })
  }

  const { onMouseOverTile } = useLinePointerNavigation({
    nodeId: focusId,
    swiper,
    setActiveSlideIndex,
  })

  const onSelected = () => {
    const tileData = sliderData[activeSlideIndex]
    const focusId = `${nodeId}_${tileData.id}_${activeSlideIndex}`
    onHandleToggle({ tileData, focusId })
  }

  return (
    <ChannelLineElem
      elementType="section"
      focusId={focusId}
      onRight={slideNext}
      onLeft={slidePrev}
      className="line"
      $isVisible
      onSelected={onSelected}
    >
      <LineTitle>{name}</LineTitle>
      {isLoaded && sliderData.length > 0 ? (
        <SliderWrapper>
          <Slider
            swiper={swiper}
            activeSlideId={activeSlideId}
            setActiveSlideIndex={setActiveSlideIndex}
            slidesPerView={channelSlidersCountInPortView}
            sliderId={focusId}
            sliderData={sliderData}
            onSwiper={onSwiper}
            initialSlide={activeSlideIndex}
            movementType={sliderMovementType.PROGRESSIVE}
            broadcastId={broadcastId}
            onMouseOverTile={onMouseOverTile}
          />
        </SliderWrapper>
      ) : (
        <ChannelLoadingItem />
      )}
    </ChannelLineElem>
  )
}
export const ChannelLine = memo(ChannelLineRaw)
